/**
 * ContinuityAssetEditor — modal for filling in the six continuity fields
 * on a single investment. Saves via PUT /api/investments/{id}.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ContinuityDocumentsSection } from './ContinuityDocumentsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTACT_FIELDS = [
  { key: 'continuity_holder_first_name', label: 'Contact — first name', placeholder: 'e.g. Jane', type: 'input' },
  { key: 'continuity_holder_last_name', label: 'Contact — last name', placeholder: 'e.g. Smith', type: 'input' },
  { key: 'continuity_holder_role', label: 'Contact — role', placeholder: 'e.g. Relationship Manager, Broker, Fund Admin', type: 'input' },
];

const FIELDS = [
  { key: 'continuity_holder_contact_phone', label: 'Contact — phone (optional)', placeholder: '+44 20 …', type: 'input' },
  { key: 'continuity_holder_contact_email', label: 'Contact — email (optional)', placeholder: 'jane@broker.example', type: 'input' },
  { key: 'continuity_paperwork_location', label: 'Where the paperwork lives', placeholder: 'e.g. Home safe, top drawer', type: 'input' },
  { key: 'continuity_access_instructions', label: 'How a Trusted Contact would access it', placeholder: 'e.g. Combination in the sealed envelope in the study', type: 'textarea' },
  { key: 'continuity_notes_for_contact', label: 'Notes for your Trusted Contact (optional)', placeholder: 'Anything else that would help them make sense of this asset', type: 'textarea' },
];

const ALL_FIELDS = [...CONTACT_FIELDS, ...FIELDS];

const pickInitial = (inv) =>
  ALL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: inv?.[f.key] || '' }), {});

export function ContinuityAssetEditor({ investment, open, onOpenChange, onSaved }) {
  const [values, setValues] = useState(() => pickInitial(investment));
  const [saving, setSaving] = useState(false);
  const [docCount, setDocCount] = useState(null);

  useEffect(() => { if (open) setValues(pickInitial(investment)); }, [open, investment]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/investments/${investment.investment_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Could not save.');
      }
      toast.success('Saved.');
      onSaved && onSaved(await res.json(), { docCount });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!investment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="continuity-editor-dialog">
        <DialogHeader>
          <DialogTitle>Continuity details for {investment.name}</DialogTitle>
          <DialogDescription>
            Optional notes that would help a Trusted Contact make sense of this asset if they ever needed to.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[26rem] overflow-y-auto pr-1">
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1">Contact person at the holder</p>
            <div className="grid grid-cols-3 gap-2">
              {CONTACT_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label htmlFor={`ce-${f.key}`} className="text-xs">{f.label.split(' — ')[1]}</Label>
                  <Input
                    id={`ce-${f.key}`} placeholder={f.placeholder}
                    value={values[f.key]}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    data-testid={`ce-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`ce-${f.key}`}>{f.label}</Label>
              {f.type === 'textarea' ? (
                <Textarea
                  id={`ce-${f.key}`} rows={2} placeholder={f.placeholder}
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  data-testid={`ce-${f.key}`}
                />
              ) : (
                <Input
                  id={`ce-${f.key}`} placeholder={f.placeholder}
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  data-testid={`ce-${f.key}`}
                />
              )}
            </div>
          ))}
          <ContinuityDocumentsSection
            investmentId={investment.investment_id}
            onCountChange={setDocCount}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ce-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ce-save">
            {saving ? 'Saving…' : 'Save continuity details'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
